import { useState, type FormEvent } from 'react';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AppDialog from '../../../components/AppDialog';
import { useModalHistory } from '../../../utils/useModalHistory';
import type { ExtensionSettings } from '../types';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';

interface ExtensionSettingsDialogProps {
  onClose: () => void;
  settings: ExtensionSettings;
  onSave: (settings: ExtensionSettings) => void;
  hasDismissed: boolean;
  onResetDismissed: () => void;
}

export default function ExtensionSettingsDialog({
  onClose,
  settings,
  onSave,
  hasDismissed,
  onResetDismissed,
}: ExtensionSettingsDialogProps) {
  useModalHistory(onClose);
  const [draft, setDraft] = useState<ExtensionSettings>(settings);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave(draft);
    onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={LABELS.extensionSettingsTitle}
      testId="extension-settings-dialog"
    >
      <form id="extension-settings-form" onSubmit={submit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary mt-0 mb-0">{LABELS.extensionSettingsDescription}</p>

          <div className="flex items-center gap-4">
            <TextField
              type="number"
              label={LABELS.extensionWindowDaysLabel}
              value={draft.windowDays}
              onChange={(e) => {
                const parsed = Number(e.target.value);
                if (Number.isFinite(parsed) && parsed >= -60 && parsed <= 60) {
                  setDraft((d) => ({ ...d, windowDays: parsed }));
                }
              }}
              slotProps={{ htmlInput: { min: -60, max: 60 } }}
              fullWidth
            />
            <Tooltip title={LABELS.extensionWindowDaysHelp} arrow>
              <InfoOutlinedIcon
                fontSize="small"
                className="text-text-secondary shrink-0"
                aria-label={LABELS.extensionWindowDaysHelp}
              />
            </Tooltip>
          </div>

          <div className="flex items-center gap-1">
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.considerOptions}
                  onChange={(e) => setDraft((d) => ({ ...d, considerOptions: e.target.checked }))}
                />
              }
              label={LABELS.extensionConsiderOptionsLabel}
            />
            <Tooltip title={LABELS.extensionConsiderOptionsHelp} arrow>
              <InfoOutlinedIcon
                fontSize="small"
                className="text-text-secondary shrink-0"
                aria-label={LABELS.extensionConsiderOptionsHelp}
              />
            </Tooltip>
          </div>

          <Divider />

          <div className="flex flex-col gap-2 items-start">
            <p className="text-sm text-text-secondary mt-0 mb-0">
              {LABELS.extensionResetDismissedDescription}
            </p>
            <Button size="small" variant="outlined" disabled={!hasDismissed} onClick={onResetDismissed}>
              {LABELS.extensionResetDismissed}
            </Button>
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-4 pt-1 gap-1">
          <Button onClick={onClose} data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <Button type="submit" variant="contained" data-testid="save-button">
            {LABELS.save}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
